import { Component, Input, OnChanges, ViewChild, TemplateRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ReportsService } from '../../services/reports.service';

/**
 * Recurring tab on the customer card — everything this customer rebuys on a
 * cycle, so a rep or counter person can see what is due before they finish the
 * order in front of them.
 *
 * Reads the nightly `recurring_candidates` snapshot. Grade is cv =
 * stddev(gap)/mean(gap): A is a cycle you can promise a date on, B is a real but
 * looser one, C is a repeat worth mentioning but not scheduling. Lapsed means
 * more than a full cycle past due — a win-back conversation, not a reorder — so
 * it is called out separately rather than shown as "very late".
 */
@Component({
	selector: 'app-customer-recurring',
	templateUrl: './customer-recurring.component.html',
	styleUrls: ['./customer-recurring.component.scss']
})
export class CustomerRecurringComponent implements OnChanges {
	@ViewChild('historyDialog', { static: true }) historyDialog!: TemplateRef<any>;

	@Input() customerdata: any;

	loading = false;
	loadedFor = '';
	message = '';

	rows: any[] = [];
	summary: any = null;

	// "Why this number" — the orders behind one item's cycle.
	history: any = null;
	historyLoading = false;

	constructor(
		private reportsService: ReportsService,
		private dialog: MatDialog,
	) {}

	ngOnChanges(): void {
		const debtorno = this.customerdata?.debtorno;
		if (debtorno && debtorno !== this.loadedFor) {
			this.load(debtorno);
		}
	}

	load(debtorno: string): void {
		this.loading = true;
		this.message = '';
		this.loadedFor = debtorno;

		this.reportsService.getRecurringCustomer(debtorno).subscribe((r: any) => {
			this.loading = false;
			if (!r?.success) {
				this.rows = [];
				this.summary = null;
				this.message = r?.message || 'Recurring data is not available.';
				return;
			}
			this.rows = r.rows || [];
			this.summary = r.summary || null;
		});
	}

	/**
	 * Show the orders and invoices the cycle was measured from — the gap column
	 * in that dialog is the series the cadence averages.
	 */
	openHistory(stkcode: string): void {
		this.history = null;
		this.historyLoading = true;
		this.dialog.open(this.historyDialog, { width: '720px', maxWidth: '95vw' });

		this.reportsService.getRecurringHistory(this.loadedFor, stkcode).subscribe((r: any) => {
			this.historyLoading = false;
			this.history = r?.success ? r : null;
		});
	}

	get dueRows(): any[] {
		return this.rows.filter(r => !r.lapsed && Number(r.days_late) >= 0);
	}

	get upcomingRows(): any[] {
		return this.rows.filter(r => !r.lapsed && Number(r.days_late) < 0);
	}

	get lapsedRows(): any[] {
		return this.rows.filter(r => !!r.lapsed);
	}

	/**
	 * How much to trust the cycle, in words rather than an A/B/C nobody can read.
	 * "Like clockwork" has to earn it — the stored grade is a coefficient of
	 * variation, and over only 3 gaps it lies: 50/71/98 days scores grade A even
	 * though the longest gap is double the shortest.
	 */
	buyingPattern(r: any): string {
		const every = 'every ' + r.cycle_days + ' days';
		const gaps = Number(r.buys) - 1;
		const spread = Number(r.max_gap) / Math.max(1, Number(r.min_gap));

		if (r.grade === 'A' && gaps >= 5 && spread < 2.5) { return 'Like clockwork, ' + every; }
		if (r.grade === 'A' || r.grade === 'B') { return 'Usually ' + every; }
		return 'Roughly ' + every;
	}

	/** Days since their last purchase, from the date — never a field that may be absent. */
	private daysSinceLast(r: any): number {
		const last = new Date(r.last_buy);
		if (isNaN(last.getTime())) { return NaN; }
		return Math.round((Date.now() - last.getTime()) / 86400000);
	}

	/** Why it's due, in one line: their rhythm, when they last bought, how late. */
	whyDue(r: any): string {
		const since = this.daysSinceLast(r);
		const last = isNaN(since) ? 'Last bought ' + r.last_buy
			: since === 0 ? 'Last bought today'
				: since === 1 ? 'Last bought yesterday'
					: 'Last bought ' + since + ' days ago';
		const late = Number(r.days_late);

		if (late > 0) { return last + ' — ' + late + ' days past due'; }
		if (late === 0) { return last + ' — due today'; }
		return last + ' — due in ' + Math.abs(late) + ' days';
	}

	lateLabel(days: number): string {
		const d = Number(days);
		if (d > 0) { return d + ' days late'; }
		if (d === 0) { return 'due today'; }
		return 'in ' + Math.abs(d) + ' days';
	}
}
